// src/pages/LoginPage.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./LoginPage.css";

import {
  loginWithEmail,
  authenticateWithGoogle,
  authenticateWithApple,
  reloadCurrentAuthUser,
  logoutUser,
} from "../services/authDataService";

import { getUserProfileData } from "../services/profileDataService";

import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import SpeechIcon from "../components/SpeechIcon";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // ✅ Neu: Terms-Hinweis für Social Login
  const [showTermsRequiredModal, setShowTermsRequiredModal] = useState(false);
  const [termsProviderLabel, setTermsProviderLabel] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const redirectAfterLogin =
    location.state?.from?.pathname
      ? `${location.state.from.pathname}${location.state.from.search || ""}`
      : location.state?.from || "/profil";

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (LoginPage):", err);
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  const clearLocalUserState = () => {
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("userProfile");
    } catch (e) {
      console.warn("LocalStorage konnte nicht bereinigt werden:", e);
    }
  };

  const initPushSafely = async () => {
    try {
      const { initPushAfterLogin } = await import("../services/pushDataService");
      await initPushAfterLogin({ force: true });
    } catch (e) {
      console.warn("Push konnte nach Login nicht gestartet werden:", e);
    }
  };

  const userHasAcceptedTerms = async (uid) => {
    if (!uid) return false;

    try {
      const profile = await getUserProfileData(uid);

      return (
        profile?.termsAccepted === true ||
        profile?.termsAcceptedAt ||
        profile?.termsVersion
      );
    } catch (error) {
      console.warn("Terms-Status konnte nicht geladen werden:", error);
      return false;
    }
  };

  const handleSocialLoginNeedsTerms = async (providerLabel) => {
    try {
      await logoutUser();
    } catch (error) {
      console.warn("Logout nach fehlender Terms-Zustimmung fehlgeschlagen:", error);
    }

    clearLocalUserState();

    setTermsProviderLabel(providerLabel || "diesem Login");
    setShowTermsRequiredModal(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!email.trim() || !password.trim()) {
      setErrorMessage("Bitte E-Mail und Passwort eingeben.");
      return;
    }

    setIsLoading(true);

    try {
      const user = await loginWithEmail({
        email: email.trim(),
        password,
      });

      if (!user?.uid) {
        throw new Error("no-user-returned");
      }

      const refreshedUser = await reloadCurrentAuthUser();

      if (!refreshedUser?.emailVerified) {
        await logoutUser();
        clearLocalUserState();

        setErrorMessage(
          "Bitte bestätige zuerst deine E-Mail-Adresse, bevor du dich einloggst."
        );
        return;
      }

      localStorage.setItem(
        "user",
        JSON.stringify({
          email: refreshedUser.email || email.trim(),
          uid: refreshedUser.uid,
          mode: "firebase",
        })
      );

      await initPushSafely();

      navigate(redirectAfterLogin, { replace: true });
    } catch (error) {
      console.error("Login fehlgeschlagen:", error);

      const code = error?.code || "";

      if (code === "auth/invalid-email") {
        setErrorMessage("Die E-Mail-Adresse ist ungültig.");
      } else if (
        code === "auth/user-not-found" ||
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential"
      ) {
        setErrorMessage(
          "E-Mail oder Passwort ist falsch. Bitte überprüfe deine Eingaben."
        );
      } else if (code === "auth/user-disabled") {
        setErrorMessage(
          "Dieser Account wurde deaktiviert. Bitte kontaktiere den Support."
        );
      } else if (code === "auth/too-many-requests") {
        setErrorMessage(
          "Zu viele Versuche. Bitte warte kurz und versuche es später erneut."
        );
      } else if (
        code === "auth/network-request-failed" ||
        code === "auth/internal-error"
      ) {
        setErrorMessage(
          "Server aktuell nicht erreichbar. Bitte prüfe deine Verbindung und versuche es erneut."
        );
      } else {
        setErrorMessage(
          "Beim Login ist ein Fehler aufgetreten. Bitte versuche es später erneut."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      const user = await authenticateWithGoogle({
        fallbackUsername: "",
        navigate,
        navigateTo: null,
      });

      if (!user?.uid) {
        throw new Error("no-user-returned");
      }

      const accepted = await userHasAcceptedTerms(user.uid);

      if (!accepted) {
        await handleSocialLoginNeedsTerms("Google");
        return;
      }

      await initPushSafely();

      navigate(redirectAfterLogin, { replace: true });
    } catch (e) {
      console.error("Google Sign-In fehlgeschlagen (LoginPage):", e);

      if (e?.code === "auth/popup-closed-by-user") {
        setErrorMessage("Google Login abgebrochen.");
      } else if (e?.code === "auth/cancelled-popup-request") {
        setErrorMessage("Google Login wurde abgebrochen. Bitte erneut versuchen.");
      } else if (e?.code === "auth/unauthorized-domain") {
        setErrorMessage(
          "Domain nicht erlaubt. Prüfe Firebase Auth → Authorized domains."
        );
      } else if (e?.code === "auth/network-request-failed") {
        setErrorMessage("Netzwerkproblem. Bitte Verbindung prüfen und erneut versuchen.");
      } else {
        setErrorMessage("Google Login fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      const user = await authenticateWithApple({
        fallbackUsername: "",
        navigate,
        navigateTo: null,
      });

      if (!user?.uid) {
        throw new Error("no-user-returned");
      }

      const accepted = await userHasAcceptedTerms(user.uid);

      if (!accepted) {
        await handleSocialLoginNeedsTerms("Apple");
        return;
      }

      console.log("🍎 LoginPage Apple success", {
        uid: user.uid,
        email: user.email,
      });

      await initPushSafely();

      navigate(redirectAfterLogin, { replace: true });
    } catch (error) {
      console.error("Apple Login fehlgeschlagen:", error);

      const code = String(error?.code || "");
      const msg = String(error?.message || "");

      if (
        code === "auth/popup-closed-by-user" ||
        msg.toLowerCase().includes("canceled") ||
        msg.toLowerCase().includes("cancelled")
      ) {
        setErrorMessage("Apple Login abgebrochen.");
      } else {
        setErrorMessage("Apple Login fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToRegisterForTerms = () => {
    setShowTermsRequiredModal(false);

    navigate("/register", {
      replace: true,
      state: {
        fromLoginTermsCheck: true,
        provider: termsProviderLabel,
      },
    });
  };

  const handleCancelTermsRedirect = () => {
    setShowTermsRequiredModal(false);
    setTermsProviderLabel("");
  };

  return (
    <div className="LoginPage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Edens Ernte Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />

      <GateIcon className="login-icon" />
      <SpeechIcon className="speech-icon no-tap-highlight" />

      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon"
        onClick={() => navigate("/rezepte")}
      />

      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      <div className="login-container">
        <h2>Hallo lieber Gartenfreund!</h2>

        <p>
          Bitte logge dich ein, um alle Funktionen von Edens Ernte nutzen zu
          können.
        </p>

        <form className="login-form" onSubmit={handleLogin}>
          <input
            type="email"
            placeholder="E-Mail-Adresse"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />

          <div className="password-container">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />

            <span
              className="toggle-password-login"
              onClick={() => setShowPassword((v) => !v)}
              title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
            >
              {showPassword ? "👁️🚫" : "👁️"}
            </span>
          </div>

          <p className="forgot-password" onClick={() => navigate("/forgot-password")}>
            Passwort vergessen?
          </p>

          <button type="submit" className="green-button" disabled={isLoading}>
            {isLoading ? "Bitte warten…" : "Einloggen"}
          </button>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="or-divider">
            <span>ODER</span>
          </div>

          <div className="one-click-options">
            <button
              className="green-button"
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              Mit Google einloggen
            </button>

            <button
              className="green-button"
              type="button"
              onClick={handleAppleLogin}
              disabled={isLoading}
            >
              Mit Apple einloggen
            </button>
          </div>
        </form>

        <div className="register-link">
          <p>
            Noch kein Konto?{" "}
            <span className="link-text" onClick={() => navigate("/register")}>
              Jetzt registrieren
            </span>
          </p>
        </div>
      </div>

      {showTermsRequiredModal && (
        <div
          className="modal-overlay"
          onClick={handleCancelTermsRedirect}
        >
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Nutzungsbedingungen erforderlich</h3>

            <p>
              Für die Nutzung von Edens Ernte musst du zuerst die
              Nutzungsbedingungen und Datenschutzbestimmungen akzeptieren.
            </p>

            <p>
              Bitte fahre über die Registrierung fort. Dort kannst du die
              Bedingungen lesen und bestätigen.
            </p>

            <div className="modal-buttons">
              <button
                type="button"
                className="green-button"
                onClick={handleGoToRegisterForTerms}
              >
                Zur Registrierung
              </button>

              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancelTermsRedirect}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;